import { Button, Icon } from '@deque/cauldron-react';
import React, { type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styles from './SelectionStep.css';

export interface SelectionStepConfig<V extends string> {
  groupName: string;
  heading: string;
  sub?: string;
  options: {
    value: V;
    label: string;
    description?: string;
    icon?: string;
    dividerBefore?: boolean;
  }[];
  backLabel?: string;
  nextLabel: string;
}

interface SelectionStepProps<V extends string> {
  config: SelectionStepConfig<V>;
  value: V;
  onChange: (value: V) => void;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  headingRef: RefObject<HTMLHeadingElement | null>;
}

const SelectionStep = <V extends string>({
  config,
  value,
  onChange,
  onBack,
  onNext,
  onCancel,
  headingRef
}: SelectionStepProps<V>): React.ReactElement => {
  const { t } = useTranslation();
  const headingId = `${config.groupName}-heading`;
  const subId = `${config.groupName}-sub`;

  return (
    <>
      <h1
        id={headingId}
        ref={headingRef}
        tabIndex={-1}
        className={styles.heading}
      >
        {config.heading}
      </h1>
      {config.sub && (
        <p id={subId} className={styles.sub}>
          {config.sub}
        </p>
      )}
      <div className={styles.options}>
        {/* One label per card, with the native radio left visible: the guided tests flag a card as a role-less control when the click target or the visible radio is anything else. */}
        <div
          role="radiogroup"
          aria-labelledby={headingId}
          aria-describedby={config.sub ? subId : undefined}
        >
          {config.options.map(option => {
            const id = `${config.groupName}-${option.value}`;
            const checked = value === option.value;
            return (
              <React.Fragment key={option.value}>
                {option.dividerBefore && (
                  <div className={styles.divider}>{t('Or')}</div>
                )}
                <label htmlFor={id} className="Radio__wrap">
                  <input
                    type="radio"
                    id={id}
                    name={config.groupName}
                    value={option.value}
                    checked={checked}
                    onChange={() => onChange(option.value)}
                    className={styles.radio}
                    aria-labelledby={`${id}-name`}
                    aria-describedby={
                      option.description ? `${id}-desc` : undefined
                    }
                  />
                  <span id={`${id}-name`} className={styles.optionName}>
                    {option.label}
                  </span>
                  {option.icon && (
                    <span className={styles.optionTile}>
                      <img
                        src={option.icon}
                        alt=""
                        aria-hidden="true"
                        className={styles.optionIcon}
                      />
                    </span>
                  )}
                  {option.description && (
                    <span id={`${id}-desc`} className="Field__labelDescription">
                      {option.description}
                    </span>
                  )}
                </label>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className={styles.actions}>
        <div className={styles.actionsPrimary}>
          {config.backLabel && (
            <Button variant="secondary" type="button" onClick={onBack}>
              <Icon type="arrow-left" />
              {config.backLabel}
            </Button>
          )}
          <Button variant="primary" type="button" onClick={onNext}>
            {config.nextLabel}
            <Icon type="arrow-right" />
          </Button>
        </div>
        <Link to="/" onClick={onCancel} className={styles.cancelLink}>
          {t('Cancel')}
        </Link>
      </div>
    </>
  );
};

export default SelectionStep;
